import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Cta } from '../Cta';
import { Reveal } from '../Reveal';
import { useScore } from '../ScoreContext';
const earlyStages = ['Helps your readers apply your ideas to their situation', 'Gives them a useful next move', 'Shows you what they need'];

/**
 * Path Author Leverage builds. Final stage is the endpoint; services are examples.
 */
function ReaderToWorkSpine() {
  const reduceMotion = useReducedMotion();
  return <div className="relative mx-auto w-fit max-w-full text-left lg:mx-0 lg:w-full lg:max-w-none" data-id="element-816" data-mp="1sem7tqoc4wyc">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-817" data-mp="1xc5mgroy3bqt">
        We build the path that
      </p>

      {/* Line terminates at the final stage marker — not beside the examples */}
      <div className="relative mt-4 lg:mt-5" data-id="element-818" data-mp="14o0060i8teov">
        <div className="absolute bottom-0 left-[0.4375rem] top-[0.7rem] w-px bg-gradient-to-b from-seaglass from-[65%] to-sand" aria-hidden="true" data-id="element-819" data-mp="19mtcvqpph1nz" />

        <ol className="relative space-y-4 lg:space-y-5" data-id="element-820" data-mp="10pwjetlzhikv">
          {earlyStages.map((label, i) => <motion.li key={label} className="grid grid-cols-[0.875rem_1fr] items-start gap-x-4" initial={reduceMotion ? false : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: reduceMotion ? 0 : 0.1 + i * 0.08,
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-821" data-mp="vs3gc9fz94ot" data-mpi={i}>
              <span className="relative z-10 mt-[0.55rem] h-2 w-2 justify-self-center rounded-full bg-seaglass ring-[3px] ring-paper" aria-hidden="true" data-id="element-822" data-mp="d027ce9hny1o" />
              <span className="font-serif text-lg leading-snug text-navy md:text-xl" data-id="element-823" data-mp="83tvb9ql8kyq">
                {label}
              </span>
            </motion.li>)}
        </ol>

        <motion.div className="relative mt-4 grid grid-cols-[0.875rem_1fr] items-start gap-x-4 lg:mt-5" initial={reduceMotion ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: reduceMotion ? 0 : 0.38,
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-824" data-mp="22fi6r1xm25ed">
          <span className="relative z-10 mt-[0.6rem] h-2.5 w-2.5 justify-self-center rounded-full bg-sand ring-[3px] ring-paper" aria-hidden="true" data-id="element-825" data-mp="3hzhptbim4zb1" />
          <div className="min-w-0" data-id="element-826" data-mp="3myc6o7x0sdsi">
            <p className="font-serif text-xl font-semibold leading-snug text-navy md:text-[1.35rem]" data-id="element-827" data-mp="eiiozpqycw9v">
              Connects them with the right way to work with you:
            </p>
            <p className="mt-2.5 text-sm font-medium leading-[1.45] text-[#c89860] md:text-base" data-id="element-828" data-mp="jezn9crv8r0o">
              Coaching · Consulting · Speaking · Training · Workshops
              <br data-id="element-829" data-mp="9hl9xm18oicl" />
              Courses · Certification · Communities · and more
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}
function ScrollCue() {
  const reduceMotion = useReducedMotion();
  return <motion.div className="mt-10 flex justify-center md:mt-12" aria-hidden="true" animate={reduceMotion ? undefined : {
    y: [0, 4, 0]
  }} transition={reduceMotion ? undefined : {
    duration: 2.2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-830" data-mp="2u7295zefqkqe">
      <ChevronDown className="h-5 w-5 text-seaglass/50" strokeWidth={1.75} data-id="element-831" data-mp="2wn0ofpi4633r" />
    </motion.div>;
}
export function HeroV2() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="relative z-10 bg-paper px-6 pb-14 pt-12 shadow-[0_14px_28px_-22px_rgba(26,40,56,0.32)] md:px-10 md:pb-16 md:pt-14" data-id="element-832" data-mp="23yv1cw3l820r">
      <div className="mx-auto max-w-6xl" data-id="element-833" data-mp="1z1ux8qqat9ca">
        <div className="mx-auto max-w-3xl text-center" data-id="element-834" data-mp="2dp7n2rn5218h">
          <Reveal data-id="element-835" data-mp="2dqr089dhzw61">
            <span className="inline-block max-w-[22rem] rounded-full border border-navy/12 bg-white px-4 py-1.5 text-center text-[0.9375rem] font-medium leading-snug text-navy/90 shadow-sm sm:max-w-none md:text-base" data-id="element-836" data-mp="2drakhwqmewn8">
              For{' '}
              <span className="whitespace-nowrap" data-id="element-837" data-mp="1galleat228hj">expert‑authors</span>
              {' '}whose work goes{' '}
              <span className="whitespace-nowrap" data-id="element-838" data-mp="jjgg1zqnmgtu">beyond the book</span>
            </span>
          </Reveal>
          <Reveal delay={0.04} data-id="element-839" data-mp="nktmoc1kmo1t">
            <h1 className="mt-5 font-serif text-[clamp(2.35rem,5vw,4rem)] leading-[1.04] tracking-[-0.02em] text-navy" data-id="element-840" data-mp="nhxp4otmy1dw">
              <span className="block" data-id="element-841" data-mp="x9tn7zvq1ri4">You wrote the book.</span>
              <span className="mt-1.5 block italic text-[#2f4a42]" data-id="element-842" data-mp="xb4pfv9iqr74">
                We build what comes next.
              </span>
            </h1>
          </Reveal>
        </div>

        <div className="mx-auto mt-10 max-w-5xl border-t border-gray-200/70 pt-9 md:mt-12 md:pt-12" data-id="element-843" data-mp="176ojec4ivft8">
          <div className="grid gap-9 md:gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-12 xl:gap-14" data-id="element-844" data-mp="1baw9odqmp3tk">
            {/* Left conversion stack — contents on mobile so path can sit between */}
            <div className="contents lg:col-start-1 lg:flex lg:flex-col lg:gap-5 lg:self-center lg:text-left" data-id="element-845" data-mp="3d29dezgv15o1">
              <Reveal delay={0.08} className="order-1 text-center lg:order-none lg:text-left" data-id="element-846" data-mp="3i0km36vf5a4o">
                <p className="mx-auto max-w-xl text-lg font-semibold leading-snug text-navy md:text-xl lg:mx-0" data-id="element-847" data-mp="3ai4bnsfmqwn7">
                  Your book gives readers the ideas.
                </p>
                <p className="mx-auto mt-2.5 max-w-xl text-base leading-relaxed text-text-muted md:text-lg lg:mx-0" data-id="element-848" data-mp="30ri48li2hcfl">
                  We turn those ideas into a path your readers can{' '}
                  <span className="whitespace-nowrap" data-id="element-849" data-mp="3tecl78fodtob">use—and</span> you can
                  keep using across your business.
                </p>
              </Reveal>

              <Reveal delay={0.12} className="order-3 flex flex-col items-center lg:order-none lg:items-start" data-id="element-850" data-mp="3jgy4r8tr54w0">
                <div className="flex w-full flex-col items-center gap-2.5 sm:w-fit" data-id="element-851" data-mp="3fg5wahspf40s">
                  <Cta variant="primary" onClick={openDiagnostic} className="min-h-[52px] px-6 py-3.5 text-base font-semibold sm:whitespace-nowrap md:text-lg" data-id="element-852" data-mp="281t2g5xj98cx">
                    Get Your Reader-to-Revenue Score
                  </Cta>
                  <p className="text-center text-sm leading-snug text-navy/60" data-id="element-853" data-mp="25iy13uqmojlw">
                    <span className="block" data-id="element-854" data-mp="2agzjedg8cewb">8 questions to see how clearly</span>
                    <span className="block" data-id="element-855" data-mp="1vjp937fbpf26">
                      reader interest connects to your business.
                    </span>
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Path — mobile order 2 (between ideas and CTA); desktop right column */}
            <Reveal delay={0.1} className="order-2 flex w-full justify-center lg:order-none lg:col-start-2 lg:row-start-1 lg:block lg:justify-start lg:self-center" data-id="element-856" data-mp="1ridiaykpqnse">
              <ReaderToWorkSpine data-id="element-857" data-mp="1cr4kw3akjy4p" />
            </Reveal>
          </div>

          <ScrollCue data-id="element-858" data-mp="1hsk5xvcqy4n3" />
        </div>
      </div>
    </section>;
}